import React, { useContext, useEffect, useRef, useState } from 'react'
import { AIResultDisplayModel } from './AIResultMapper'
import classNames from 'classnames'
import { EditableText } from './common/EditableText/EditableText'
import { AIContextContext } from './state/context'
import { TrackEvent, useTracking } from './common/tracking'
import { RoomSessionContext } from './models'
import { getAIResultTrackingDetails, getRoomAndAIFeatureTrackingDetails } from './tracking'
import { AIFeature } from '@zocks-communications/client-sdk-js'
import { Form } from 'react-bootstrap'
export const ResultViewer: React.FC<{
  aiFeature: AIFeature
  aiResult: AIResultDisplayModel | AIResultDisplayModel[]
  onCopyAction?: (aiResult: AIResultDisplayModel) => Promise<void>
  onDismissAction?: (aiResult: AIResultDisplayModel) => void
  hasParent?: boolean
  onAIResultChanged?: (changedResult: string, changedAIResultDisplayId: string) => Promise<void>
  onSelectAIResult?: (aiResult: AIResultDisplayModel) => void
  onSelectTaskAIResult?: (checked: boolean, aiResult: AIResultDisplayModel) => void
}> = ({ aiFeature, aiResult, onCopyAction, onDismissAction, onAIResultChanged, onSelectAIResult, onSelectTaskAIResult }) => {
  const textRef = useRef<HTMLDivElement>(null)
  const [rows, setRows] = useState(2)
  const {
    state: { aiFeatureIdInEdit },
  } = useContext(AIContextContext)
  const { trackingClient } = useTracking()
  const roomSession = useContext(RoomSessionContext)
  useEffect(() => {
    if (textRef && textRef.current) {
      const divHeight = textRef.current.offsetHeight
      const lines = Math.max(Math.round(divHeight / 16), 2)
      setRows(lines)
    }
  }, [textRef])
  const onAIResultChangeStarted = (aiResult: AIResultDisplayModel) => {
    trackingClient.track({
      eventName: TrackEvent.AI_RESULT_OVERRIDE,
      eventDetails: {
        ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
        ...getAIResultTrackingDetails(aiResult),
        actionStatus: 'started',
      },
    })
  }
  const onAIResultChangeCancelled = (aiResult: AIResultDisplayModel) => {
    trackingClient.track({
      eventName: TrackEvent.AI_RESULT_OVERRIDE,
      eventDetails: {
        ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
        ...getAIResultTrackingDetails(aiResult),
        actionStatus: 'cancelled',
      },
    })
  }
  if (!aiResult) {
    return <></>
  }
  return (
    <>
      {Array.isArray(aiResult) ? (
        <>
          {aiResult.length ? (
            <ul className={classNames('ai-result-list', `level-${aiResult[0].level}`)}>
              {aiResult.map((result) => {
                return (
                  <li
                    key={`array_${result.displayId}`}
                    className={classNames('ai-result-list-item', {
                      leaf: result.isLeaf,
                      'ai-result-content--selected': result.selected,
                    })}
                  >
                    {result.level === 2 && !result.isLeaf && (
                      <div className='ai-result-actions'>
                        {!aiFeatureIdInEdit ? (
                          <>
                            {onCopyAction ? (
                              <div
                                className='cursor-pointer'
                                onClick={async () => {
                                  await onCopyAction(result)
                                }}
                              >
                                <i className='bi bi-copy' />
                              </div>
                            ) : null}
                            {onDismissAction ? (
                              <div
                                className='cursor-pointer'
                                onClick={() => {
                                  onDismissAction(result)
                                }}
                              >
                                {result.dismissed ? <i className='bi bi-check2' /> : <i className='bi bi-x' />}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <div
                            className='cursor-pointer'
                            onClick={() => {
                              onSelectAIResult && onSelectAIResult(result)
                            }}
                          >
                            {result.selected ? <i className='bi bi-dash-circle' /> : <i className='bi bi-plus-circle' />}
                          </div>
                        )}
                      </div>
                    )}
                    <div
                      className={classNames('ai-result-content w-100', {
                        'ai-result-content--dismissed': result.dismissed,
                      })}
                    >
                      <ResultViewer
                        aiFeature={aiFeature}
                        aiResult={result}
                        onCopyAction={onCopyAction}
                        onDismissAction={onDismissAction}
                        hasParent={true}
                        onAIResultChanged={onAIResultChanged}
                        onSelectAIResult={onSelectAIResult}
                        onSelectTaskAIResult={onSelectTaskAIResult}
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className='p-3'>No relevant information detected</div>
          )}
        </>
      ) : typeof aiResult.result === 'object' ? (
        <>
          {Object.entries(aiResult.result).map(([key, value], idx) => {
            return (
              <div className='ai-result-map-container' key={`map_${idx}`}>
                <div className='ai-result-title lead-small'>
                  <strong>{key}</strong>
                </div>
                <ResultViewer
                  aiFeature={aiFeature}
                  aiResult={value}
                  onCopyAction={onCopyAction}
                  onDismissAction={onDismissAction}
                  hasParent={true}
                  onAIResultChanged={onAIResultChanged}
                  onSelectAIResult={onSelectAIResult}
                  onSelectTaskAIResult={onSelectTaskAIResult}
                />
              </div>
            )
          })}
        </>
      ) : (
        <div
          className='ai-summary-result-container'
          ref={textRef}
          onClick={() => {
            onSelectTaskAIResult && onSelectTaskAIResult(!aiResult.selected, aiResult)
          }}
        >
          {onSelectTaskAIResult ? (
            <div>
              <Form.Check className='form-check' checked={aiResult.selected} />
            </div>
          ) : null}
          {onAIResultChanged && !aiFeatureIdInEdit ? (
            <EditableText
              text={aiResult.result.toString()}
              textOverflow={false}
              inputPlaceHolder={'Enter the new result.'}
              onEdit={() => onAIResultChangeStarted(aiResult)}
              onCancel={() => onAIResultChangeCancelled(aiResult)}
              onChange={async (changed) => {
                await onAIResultChanged(changed, aiResult.displayId)
              }}
              rows={rows}
            />
          ) : (
            <div>{aiResult.result.toString()}</div>
          )}
        </div>
      )}
    </>
  )
}
